import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Budget } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface BudgetsState {
  budgets: Budget[];
  currentBudget: Budget | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: BudgetsState = {
  budgets: [],
  currentBudget: null,
  isLoading: false,
  error: null,
};

// [T71P1] Optional filter params for multi-budget per month
export interface FetchBudgetsParams {
  year?: number;
  month?: number;
}

/** Frontend form shape for create/update — maps to backend API payload */
export interface BudgetApiPayload {
  name?: string;
  month?: number;
  year?: number;
  budgetMethod?: 'zero-based' | 'spending-plan' | 'rollover';
  method?: string;
  plannedIncome?: number;
  notes?: string;
  emoji?: string;
  color?: string;
  /** Frontend sends categoryId, we remap to `category` before POST/PUT */
  categoryBudgets?: Array<{ categoryId: string; budgeted: number }>;
}

/** Maps frontend BudgetFormData shape to the API payload shape */
function toBudgetApiPayload(data: BudgetApiPayload): Record<string, unknown> {
  const { categoryBudgets, budgetMethod, ...rest } = data;
  return {
    ...rest,
    // Backend accepts `method` or `budgetMethod` — send both for compat
    method: budgetMethod ?? rest.method,
    budgetMethod,
    categoryBudgets: (categoryBudgets ?? [])
      .filter((cb) => cb.categoryId)
      .map((cb) => ({ category: cb.categoryId, budgeted: cb.budgeted })),
  };
}

// Async thunks
export const fetchBudgets = createAsyncThunk(
  'budgets/fetchBudgets',
  async (params: FetchBudgetsParams | undefined, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.year) queryParams.set('year', String(params.year));
      if (params?.month) queryParams.set('month', String(params.month));
      const queryString = queryParams.toString();
      const url = queryString
        ? `${endpoints.budgets.list}?${queryString}`
        : endpoints.budgets.list;
      const response = await api.get(url);
      // Backend returns { success, data: [...], total, page, totalPages }
      return (response.data.data || response.data) as Budget[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch budgets'));
    }
  }
);

export const fetchCurrentBudget = createAsyncThunk(
  'budgets/fetchCurrentBudget',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.budgets.current);
      return (response.data.data || response.data) as Budget;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch current budget'));
    }
  }
);

export const createBudget = createAsyncThunk(
  'budgets/createBudget',
  async (budgetData: BudgetApiPayload, { rejectWithValue }) => {
    try {
      const payload = toBudgetApiPayload(budgetData);
      const response = await api.post(endpoints.budgets.create, payload);
      return (response.data.data || response.data) as Budget;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create budget'));
    }
  }
);

export const updateBudget = createAsyncThunk(
  'budgets/updateBudget',
  async ({ id, data }: { id: string; data: BudgetApiPayload }, { rejectWithValue }) => {
    try {
      const payload = toBudgetApiPayload(data);
      const response = await api.put(endpoints.budgets.update(id), payload);
      return (response.data.data || response.data) as Budget;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update budget'));
    }
  }
);

export const deleteBudget = createAsyncThunk(
  'budgets/deleteBudget',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.budgets.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete budget'));
    }
  }
);

// Slice
const budgetsSlice = createSlice({
  name: 'budgets',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentBudget: (state, action: PayloadAction<Budget>) => {
      state.currentBudget = action.payload;
    },
    updateCategoryBudget: (
      state,
      action: PayloadAction<{ budgetId: string; categoryId: string; budgeted: number }>
    ) => {
      const budget = state.budgets.find(b => b.id === action.payload.budgetId);
      if (budget) {
        const category = budget.categories.find(c => c.categoryId === action.payload.categoryId);
        if (category) {
          category.budgeted = action.payload.budgeted;
        }
      }
      if (state.currentBudget?.id === action.payload.budgetId) {
        const category = state.currentBudget.categories.find(
          c => c.categoryId === action.payload.categoryId
        );
        if (category) {
          category.budgeted = action.payload.budgeted;
        }
      }
    },
    // [T68P1-c] Optimistic update actions for offline sync
    addOptimisticBudget: (state, action: PayloadAction<Budget>) => {
      state.budgets.unshift(action.payload);
    },
    replaceOptimisticBudget: (state, action: PayloadAction<{ optimisticId: string; realItem: Budget }>) => {
      const idx = state.budgets.findIndex(b => b.id === action.payload.optimisticId);
      if (idx !== -1) state.budgets[idx] = action.payload.realItem;
    },
    removeOptimisticBudget: (state, action: PayloadAction<string>) => {
      state.budgets = state.budgets.filter(b => b.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch budgets
    builder
      .addCase(fetchBudgets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBudgets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.budgets = action.payload;
      })
      .addCase(fetchBudgets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch current budget
    builder
      .addCase(fetchCurrentBudget.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCurrentBudget.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentBudget = action.payload;
      })
      .addCase(fetchCurrentBudget.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create budget
    builder
      .addCase(createBudget.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBudget.fulfilled, (state, action) => {
        state.isLoading = false;
        state.budgets.push(action.payload);
      })
      .addCase(createBudget.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update budget
    builder
      .addCase(updateBudget.fulfilled, (state, action) => {
        const index = state.budgets.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.budgets[index] = action.payload;
        }
        if (state.currentBudget?.id === action.payload.id) {
          state.currentBudget = action.payload;
        }
      })
      .addCase(updateBudget.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete budget
    builder
      .addCase(deleteBudget.fulfilled, (state, action) => {
        state.budgets = state.budgets.filter(b => b.id !== action.payload);
        if (state.currentBudget?.id === action.payload) {
          state.currentBudget = null;
        }
      })
      .addCase(deleteBudget.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError, setCurrentBudget, updateCategoryBudget,
  addOptimisticBudget, replaceOptimisticBudget, removeOptimisticBudget,
} = budgetsSlice.actions;
export default budgetsSlice.reducer;
